/**
 * SlugAPI - Slug validation and management for custom tour URLs
 *
 * Supports two types of slugs:
 * - Global slugs (Pro only): xrgallery.online/my-cool-tour
 * - User slugs (all users): xrgallery.online/username/tour-name
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Logger } from '../utils/Logger';

const logger = Logger.getLogger('SlugAPI');

/**
 * Reserved slugs that cannot be used (routes, common words, etc.)
 * These are paths that conflict with app routes or are too generic
 */
export const RESERVED_SLUGS: string[] = [
  // App routes
  'dashboard',
  'api',
  'demo',
  'discover',
  'blog',
  'docs',
  'login',
  'signup',
  'register',
  'logout',
  'auth',
  'oauth',
  'callback',
  'newsletter',
  'admin',
  'settings',
  'account',
  'profile',
  'user',
  'users',
  'help',
  'support',
  'contact',
  'about',
  'terms',
  'privacy',
  'legal',
  'pricing',
  'plans',
  'upgrade',
  'billing',
  'checkout',
  'payment',
  'subscribe',
  'subscription',

  // Common reserved words
  'app',
  'web',
  'www',
  'mail',
  'email',
  'ftp',
  'cdn',
  'static',
  'assets',
  'images',
  'img',
  'media',
  'files',
  'download',
  'downloads',
  'upload',
  'uploads',

  // Generic terms
  'tour',
  'tours',
  'vr',
  'xr',
  'gallery',
  'galleries',
  'experience',
  'experiences',
  'virtual',
  '360',
  'panorama',
  'test',
  'testing',
  'dev',
  'development',
  'staging',
  'prod',
  'production',

  // Brand protection
  'xrgallery',
  'webxr',
  'anthropic',
  'claude',
  'official',
  'verified',
];

/**
 * Slug validation result
 */
export interface SlugValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate a slug for format compliance
 *
 * Rules:
 * - Length: 3-50 characters
 * - Characters: lowercase a-z, 0-9, hyphens
 * - No consecutive hyphens
 * - Must start and end with alphanumeric
 * - Not in reserved list
 * - Not matching Firestore ID pattern (20 char alphanumeric)
 */
export function validateSlug(slug: string): SlugValidationResult {
  // Trim and lowercase
  const normalizedSlug = slug.trim().toLowerCase();

  // Length check
  if (normalizedSlug.length < 3) {
    return { valid: false, error: 'Slug must be at least 3 characters' };
  }
  if (normalizedSlug.length > 50) {
    return { valid: false, error: 'Slug must be 50 characters or less' };
  }

  // Character check (a-z, 0-9, hyphens only)
  if (!/^[a-z0-9-]+$/.test(normalizedSlug)) {
    return { valid: false, error: 'Slug can only contain lowercase letters, numbers, and hyphens' };
  }

  // Must start with letter or number
  if (!/^[a-z0-9]/.test(normalizedSlug)) {
    return { valid: false, error: 'Slug must start with a letter or number' };
  }

  // Must end with letter or number
  if (!/[a-z0-9]$/.test(normalizedSlug)) {
    return { valid: false, error: 'Slug must end with a letter or number' };
  }

  // No consecutive hyphens
  if (/--/.test(normalizedSlug)) {
    return { valid: false, error: 'Slug cannot contain consecutive hyphens' };
  }

  // Reserved slug check
  if (RESERVED_SLUGS.includes(normalizedSlug)) {
    return { valid: false, error: 'This URL is reserved and cannot be used' };
  }

  // Prevent slugs that look like Firestore auto-generated IDs
  // Firestore IDs are typically 20 characters, alphanumeric
  if (/^[a-zA-Z0-9]{20}$/.test(normalizedSlug)) {
    return { valid: false, error: 'This URL format is reserved for system use' };
  }

  return { valid: true };
}

/**
 * Check if a global slug is available
 * Returns true if the slug is not taken
 */
export async function checkGlobalSlugAvailability(slug: string): Promise<boolean> {
  try {
    const normalizedSlug = slug.trim().toLowerCase();

    // First validate format
    const validation = validateSlug(normalizedSlug);
    if (!validation.valid) {
      return false;
    }

    // Check the global slugs collection
    const slugRef = doc(db, 'slugs', normalizedSlug);
    const slugDoc = await getDoc(slugRef);

    return !slugDoc.exists();
  } catch (error) {
    logger.error('Error checking global slug availability:', error);
    throw new Error('Failed to check slug availability');
  }
}

/**
 * Check if a user slug is available for a specific user
 * User slugs only need to be unique within that user's tours
 */
export async function checkUserSlugAvailability(userId: string, slug: string): Promise<boolean> {
  try {
    const normalizedSlug = slug.trim().toLowerCase();

    // First validate format
    const validation = validateSlug(normalizedSlug);
    if (!validation.valid) {
      return false;
    }

    // Query sequences for this user with the same userSlug
    const sequencesRef = collection(db, 'sequences');
    const q = query(
      sequencesRef,
      where('userId', '==', userId),
      where('userSlug', '==', normalizedSlug)
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.empty;
  } catch (error) {
    logger.error('Error checking user slug availability:', error);
    throw new Error('Failed to check slug availability');
  }
}

/**
 * Generate a URL-friendly slug from a tour name
 *
 * Examples:
 * - "My Cool Tour" -> "my-cool-tour"
 * - "360° Museum Experience!" -> "360-museum-experience"
 * - "Tour #123" -> "tour-123"
 */
export function generateSlugFromName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    // Replace special characters with their closest ASCII equivalent or remove
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    // Replace common symbols with words or nothing
    .replace(/°/g, '')
    .replace(/&/g, 'and')
    .replace(/@/g, 'at')
    .replace(/#/g, '')
    .replace(/\$/g, '')
    .replace(/%/g, '')
    // Replace spaces and underscores with hyphens
    .replace(/[\s_]+/g, '-')
    // Remove any remaining non-alphanumeric characters (except hyphens)
    .replace(/[^a-z0-9-]/g, '')
    // Remove consecutive hyphens
    .replace(/-+/g, '-')
    // Remove leading/trailing hyphens
    .replace(/^-|-$/g, '')
    // Ensure minimum length (pad if needed)
    .substring(0, 50); // Max 50 chars
}

/**
 * Get sequence by global slug
 */
export async function getSequenceByGlobalSlug(slug: string): Promise<{ sequenceId: string; userId: string } | null> {
  try {
    const normalizedSlug = slug.trim().toLowerCase();
    const slugRef = doc(db, 'slugs', normalizedSlug);
    const slugDoc = await getDoc(slugRef);

    if (!slugDoc.exists()) {
      return null;
    }

    const data = slugDoc.data();
    return {
      sequenceId: data.sequenceId,
      userId: data.userId,
    };
  } catch (error) {
    logger.error('Error getting sequence by global slug:', error);
    return null;
  }
}

/**
 * Get sequence by user slug
 */
export async function getSequenceByUserSlug(userId: string, slug: string): Promise<string | null> {
  try {
    const normalizedSlug = slug.trim().toLowerCase();
    const sequencesRef = collection(db, 'sequences');
    const q = query(
      sequencesRef,
      where('userId', '==', userId),
      where('userSlug', '==', normalizedSlug)
    );
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    return querySnapshot.docs[0].id;
  } catch (error) {
    logger.error('Error getting sequence by user slug:', error);
    return null;
  }
}

/**
 * Normalize a slug (trim and lowercase)
 */
export function normalizeSlug(slug: string): string {
  return slug.trim().toLowerCase();
}
